import { useCallback, useEffect, useState } from 'react';
import { ConfirmDeleteDialog } from '../admin/confirmDeleteDialog';
import { ApiRequestError } from '../api/apiRequestError';
import { getAdminCreators } from '../api/getAdminCreators';
import { getAdminStats } from '../api/getAdminStats';
import { getAdminTests } from '../api/getAdminTests';
import { postAdminDelete } from '../api/postAdminDelete';

type AdminTestsResponse = Awaited<ReturnType<typeof getAdminTests>>;
type Stats = Awaited<ReturnType<typeof getAdminStats>>['stats'];
type Creator = Awaited<ReturnType<typeof getAdminCreators>>['creators'][number];
type AdminTest = NonNullable<AdminTestsResponse['tests']>[number];
type Reviewer = NonNullable<AdminTestsResponse['reviewers']>[number];

type PendingDelete =
  | { kind: 'test'; id: string; label: string }
  | { kind: 'creator'; id: string; label: string; email: string }
  | { kind: 'reviewer'; id: string; label: string };

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

// /admin — deletion only (spec §5).
//
// A signed-out or non-admin visitor never reaches this: every admin API answers
// 404, and this screen renders the same "nothing here" page the router shows for
// any unknown path. The screen must not confirm that an admin surface exists.
//
// NOTE WHAT IS ABSENT. There is no reaction data anywhere on this screen, and no
// route it calls returns any. The admin sees that a reviewer exists and can
// delete them; the admin never sees what they tapped (§5.1). Deletion does not
// require inspection.
export function AdminConsoleScreen() {
  const [creators, setCreators] = useState<Creator[] | null>(null);
  const [tests, setTests] = useState<AdminTest[] | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [reviewers, setReviewers] = useState<Reviewer[] | null>(null);
  const [openTestId, setOpenTestId] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingDelete | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [creatorList, testList, statsResult] = await Promise.all([
        getAdminCreators(),
        getAdminTests(),
        getAdminStats()
      ]);
      setCreators(creatorList.creators);
      setTests(testList.tests ?? []);
      setStats(statsResult.stats);
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.status === 404) {
        setForbidden(true);
        return;
      }
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not load.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openReviewers = async (testId: string) => {
    if (openTestId === testId) {
      setOpenTestId(null);
      setReviewers(null);
      return;
    }
    setOpenTestId(testId);
    setReviewers(null);
    try {
      setReviewers((await getAdminTests(testId)).reviewers ?? []);
    } catch {
      setReviewers([]);
    }
  };

  const confirmDelete = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      if (pending.kind === 'test') await postAdminDelete('delete-test', { test_id: pending.id });
      if (pending.kind === 'reviewer') await postAdminDelete('delete-reviewer', { reviewer_id: pending.id });
      if (pending.kind === 'creator') {
        await postAdminDelete('delete-creator', { creator_id: pending.id, confirm_email: pending.email });
      }
      setPending(null);
      setOpenTestId(null);
      setReviewers(null);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Delete failed.');
      setPending(null);
    } finally {
      setBusy(false);
    }
  };

  // Identical to the router's unknown-path page. A distinct "forbidden" screen
  // would confirm the route exists (spec §5).
  if (forbidden) {
    return (
      <main className="pageWrap">
        <h1>Nothing here</h1>
        <p className="pageSub">That address doesn't match anything.</p>
        <a className="buttonSecondary buttonLink" href="/">
          Go to whatlanded
        </a>
      </main>
    );
  }

  if (!creators || !tests || !stats) {
    return (
      <main className="pageWrap">
        <p className="pageSub">{error ?? 'Loading…'}</p>
      </main>
    );
  }

  return (
    <main className="pageWrap pageWrapWide">
      <h1>Admin</h1>
      <p className="pageSub">Deletion only. Reaction data is never shown here, by design.</p>
      {error ? <div className="calloutError">{error}</div> : null}

      {/* Instance totals. Counts only — nothing here narrows toward an individual
          (Cowork015 §1). Database size earns its place because Neon's free tier
          is the one resource with a hard ceiling, and samples are what will fill
          it: one every 500ms per reviewer. */}
      {stats ? (
        <div className="statGrid">
          <div><b>{stats.creators}</b>creators</div>
          <div><b>{stats.tests}</b>tests</div>
          <div><b>{stats.tests_owned}</b>owned</div>
          <div><b>{stats.tests_anonymous}</b>anonymous</div>
          <div><b>{stats.tests_on_a_timer}</b>on a timer</div>
          <div><b>{stats.reviewers}</b>reviewers</div>
          <div><b>{stats.reactions}</b>reactions</div>
          <div><b>{stats.samples}</b>samples</div>
          <div className={stats.database_bytes > 400_000_000 ? 'statAlarm' : ''}>
            <b>{stats.database_size_pretty}</b>of 0.5 GB
          </div>
        </div>
      ) : null}

      <h2 className="mySection">Creators ({creators.length})</h2>
      <table className="adminTable">
        <thead>
          <tr>
            <th>Email</th>
            <th>Joined</th>
            <th>Tests</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {creators.map((creator) => (
            <tr key={creator.creator_id}>
              <td>
                {creator.email}
                {creator.is_admin ? <span className="adminBadge">admin</span> : null}
              </td>
              <td>{formatDate(creator.created_at)}</td>
              <td>{creator.test_count}</td>
              <td>
                <button
                  className="buttonDangerSmall"
                  type="button"
                  onClick={() =>
                    setPending({
                      kind: 'creator',
                      id: creator.creator_id,
                      label: creator.email,
                      email: creator.email
                    })
                  }
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2 className="mySection">Tests ({tests.length})</h2>
      <table className="adminTable">
        <thead>
          <tr>
            <th>Title</th>
            <th>Owner</th>
            <th>Reviewers</th>
            <th>Expires</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {tests.map((test) => (
            <tr key={test.test_id}>
              <td>
                <button className="linkButton" type="button" onClick={() => void openReviewers(test.test_id)}>
                  {test.title ?? test.test_id}
                </button>
              </td>
              <td>{test.owner_email ?? <span className="mutedNote">anonymous</span>}</td>
              <td>{test.reviewer_count}</td>
              <td>{test.expires_at ? formatDate(test.expires_at) : <span className="mutedNote">never</span>}</td>
              <td>
                <button
                  className="buttonDangerSmall"
                  type="button"
                  onClick={() =>
                    setPending({ kind: 'test', id: test.test_id, label: test.title ?? test.test_id })
                  }
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {openTestId && reviewers ? (
        <>
          <h2 className="mySection">Reviewers on that test</h2>
          <p className="mutedNote">
            Counts only. What each reviewer tapped is not shown here and is not returned by the
            server — deleting them does not require seeing it.
          </p>
          <table className="adminTable">
            <tbody>
              {reviewers.length === 0 ? (
                <tr>
                  <td className="mutedNote">No reviewers.</td>
                </tr>
              ) : (
                reviewers.map((reviewer) => (
                  <tr key={reviewer.reviewer_id}>
                    <td>{reviewer.display_name ?? <span className="mutedNote">no name given</span>}</td>
                    <td>{formatDate(reviewer.started_at)}</td>
                    <td>{reviewer.finished ? 'finished' : 'did not finish'}</td>
                    <td>{reviewer.reaction_count} reactions</td>
                    <td>
                      <button
                        className="buttonDangerSmall"
                        type="button"
                        onClick={() =>
                          setPending({
                            kind: 'reviewer',
                            id: reviewer.reviewer_id,
                            label: reviewer.display_name ?? 'this reviewer'
                          })
                        }
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </>
      ) : null}

      {pending ? (
        <ConfirmDeleteDialog
          title={`Delete ${pending.label}?`}
          detail={
            pending.kind === 'creator'
              ? 'This deletes the account, every test they own, and all reviewers, reactions and samples underneath. It cannot be undone.'
              : pending.kind === 'test'
                ? 'This deletes the test and every reviewer, reaction and sample on it. It cannot be undone.'
                : 'This deletes the reviewer and everything they contributed. It cannot be undone.'
          }
          {...(pending.kind === 'creator' ? { requireTyped: pending.email } : {})}
          busy={busy}
          onCancel={() => setPending(null)}
          onConfirm={() => void confirmDelete()}
        />
      ) : null}
    </main>
  );
}
